import { useCallback, useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Check, X } from "lucide-react";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({});

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/products");
      setProducts(r.data.products);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const toggleStock = async (id, in_stock) => {
    try {
      await adminApi.patch(`/admin/products/${id}`, { in_stock: !in_stock });
      toast.success(in_stock ? "Mis en rupture" : "Remis en stock");
      fetchProducts();
    } catch {
      toast.error("Erreur");
    }
  };

  const savePrice = async (id) => {
    const price = parseFloat(editing[id]);
    if (Number.isNaN(price) || price < 0) {
      toast.error("Prix invalide");
      return;
    }
    try {
      await adminApi.patch(`/admin/products/${id}`, { price });
      toast.success("Prix mis à jour");
      setEditing((e) => {
        const c = { ...e };
        delete c[id];
        return c;
      });
      fetchProducts();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="text-center md:text-left">
        <h1 className="font-display text-4xl">Produits</h1>
        <p className="text-amyu-muted mt-2">Active/désactive un produit, ajuste les prix.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-coral" />
        </div>
      ) : (
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {products.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-product-${p.id}`}
              className="bg-white rounded-[24px] p-5 flex flex-col sm:flex-row sm:items-center gap-4 text-center sm:text-left"
            >
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-xl break-words">{p.name}</h3>
                <div className="mt-3 flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                  <span className="text-xs uppercase tracking-widest text-amyu-muted">Prix</span>
                  {editing[p.id] !== undefined ? (
                    <>
                      <input
                        type="number"
                        step="0.10"
                        min="0"
                        value={editing[p.id]}
                        onChange={(e) => setEditing((s) => ({ ...s, [p.id]: e.target.value }))}
                        data-testid={`admin-product-price-input-${p.id}`}
                        className="bg-amyu-cream rounded-full px-3 py-1.5 w-24 outline-none focus:ring-2 focus:ring-coral/30"
                      />
                      <button
                        onClick={() => savePrice(p.id)}
                        data-testid={`admin-product-price-save-${p.id}`}
                        className="w-8 h-8 rounded-full bg-coral text-white inline-flex items-center justify-center"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={() =>
                          setEditing((s) => {
                            const c = { ...s };
                            delete c[p.id];
                            return c;
                          })
                        }
                        className="w-8 h-8 rounded-full bg-amyu-cream inline-flex items-center justify-center"
                      >
                        <X size={14} />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setEditing((s) => ({ ...s, [p.id]: p.price.toFixed(2) }))}
                      data-testid={`admin-product-price-edit-${p.id}`}
                      className="text-coral font-medium hover:underline"
                    >
                      {p.price.toFixed(2).replace(".", ",")} €
                    </button>
                  )}
                </div>
              </div>
              <button
                onClick={() => toggleStock(p.id, p.in_stock)}
                data-testid={`admin-product-stock-${p.id}`}
                className={`self-center pill px-4 py-2 text-xs uppercase tracking-[0.16em] font-medium transition-colors whitespace-nowrap ${
                  p.in_stock
                    ? "bg-amyu-pistachio text-amyu-ink"
                    : "bg-amyu-pink text-coral"
                }`}
              >
                {p.in_stock ? "En stock" : "Rupture"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
